import "@fortawesome/fontawesome-free/js/all";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PublicSecureFileBrandHeader from "../../components/Shared/PublicSecureFileBrandHeader";
import {
  downloadPublicSecureFile,
  getPublicSecureFileInfo,
  verifyPublicSecureFileAccess,
} from "../../services/secureFiles";
import "./styles.css";

const PublicSecureFilePage = () => {
  const { token } = useParams();
  const [fileInfo, setFileInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [accessSession, setAccessSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadFileInfo = async () => {
      setIsLoading(true);
      setError("");
      const { data, error: infoError } = await getPublicSecureFileInfo(token);
      if (infoError) {
        setError(infoError.message);
        setFileInfo(null);
      } else {
        setFileInfo(data);
      }
      setIsLoading(false);
    };

    loadFileInfo();
  }, [token]);

  const handleVerify = async (event) => {
    event.preventDefault();
    setIsVerifying(true);
    setError("");

    const { data, error: verifyError } = await verifyPublicSecureFileAccess({
      shareToken: token,
      password,
    });

    if (verifyError) {
      setError(verifyError.message);
      setAccessSession(null);
    } else {
      setAccessSession(data);
    }

    setIsVerifying(false);
  };

  const handleDownload = async () => {
    if (!accessSession?.accessToken) {
      return;
    }

    setIsDownloading(true);
    setError("");

    const { data, error: downloadError } = await downloadPublicSecureFile({
      shareToken: token,
      accessToken: accessSession.accessToken,
    });

    if (downloadError) {
      setError(downloadError.message);
      setIsDownloading(false);
      return;
    }

    const blobUrl = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = accessSession.fileName || fileInfo?.fileName || "secure-file";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    setIsDownloading(false);
  };

  const renderLoading = () => (
    <div className="public-secure-file-state">
      <div className="public-secure-file-spinner" aria-hidden="true" />
      <p className="public-secure-file-state-title">Loading secure document</p>
      <p className="public-secure-file-state-copy">Please wait while we verify this link.</p>
    </div>
  );

  const renderNotFound = () => (
    <div className="public-secure-file-state public-secure-file-state--error">
      <div className="public-secure-file-state-icon">
        <i className="fas fa-circle-exclamation" />
      </div>
      <p className="public-secure-file-state-title">Document unavailable</p>
      <p className="public-secure-file-state-copy">{error || "This secure link could not be found."}</p>
    </div>
  );

  const renderExpired = () => (
    <div className="public-secure-file-state public-secure-file-state--warning">
      <div className="public-secure-file-state-icon">
        <i className="fas fa-clock" />
      </div>
      <p className="public-secure-file-state-title">Link no longer active</p>
      <p className="public-secure-file-state-copy">
        This share link has expired or been revoked. Please contact Telecare to request a new link.
      </p>
    </div>
  );

  const renderVerified = () => (
    <div className="public-secure-file-state public-secure-file-state--success">
      <div className="public-secure-file-state-icon">
        <i className="fas fa-circle-check" />
      </div>
      <p className="public-secure-file-state-title">Identity verified</p>
      <p className="public-secure-file-state-copy">
        You may now download <strong>{accessSession.fileName}</strong>.
      </p>
      {error && <p className="public-secure-file-inline-error">{error}</p>}
      <button
        type="button"
        className={`public-secure-file-button public-secure-file-button--primary ${isDownloading ? "is-loading" : ""}`}
        onClick={handleDownload}
        disabled={isDownloading}
      >
        <i className="fas fa-download" aria-hidden="true" />
        Download File
      </button>
    </div>
  );

  const renderVerifyForm = () => (
    <div className="public-secure-file-panel">
      <div className="public-secure-file-document">
        <div className="public-secure-file-document-icon">
          <i className="fas fa-file-medical" aria-hidden="true" />
        </div>
        <div>
          <p className="public-secure-file-document-label">Document</p>
          <p className="public-secure-file-document-name">{fileInfo.fileName}</p>
        </div>
      </div>

      <div className="public-secure-file-notice">
        <i className="fas fa-shield-halved" aria-hidden="true" />
        <p>
          {fileInfo.patientDisplayName ? (
            <>
              Enter <strong>{fileInfo.patientDisplayName}</strong>&apos;s date of birth as <strong>DDMMYYYY</strong> to
              access this protected file.
            </>
          ) : (
            <>
              Enter the patient&apos;s date of birth as <strong>DDMMYYYY</strong> to access this protected file.
            </>
          )}
        </p>
      </div>

      <form className="public-secure-file-form" onSubmit={handleVerify}>
        <label className="public-secure-file-label" htmlFor="secure-file-password">
          Date of birth
        </label>
        <input
          id="secure-file-password"
          className={`public-secure-file-input ${error ? "has-error" : ""}`}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          pattern="[0-9]*"
          maxLength={8}
          placeholder="DDMMYYYY"
          value={password}
          onChange={(event) => {
            setError("");
            setPassword(event.target.value.replace(/\D/g, ""));
          }}
          required
        />
        {error && (
          <p className="public-secure-file-inline-error" role="alert">
            <i className="fas fa-circle-exclamation" aria-hidden="true" />
            {error}
          </p>
        )}
        <button
          type="submit"
          className={`public-secure-file-button public-secure-file-button--primary ${isVerifying ? "is-loading" : ""}`}
          disabled={isVerifying || password.length !== 8}
        >
          Verify &amp; Continue
        </button>
      </form>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return renderLoading();
    }
    if (!fileInfo) {
      return renderNotFound();
    }
    if (fileInfo.expired) {
      return renderExpired();
    }
    if (accessSession) {
      return renderVerified();
    }
    return renderVerifyForm();
  };

  return (
    <div className="public-secure-file-page">
      <div className="public-secure-file-layout">
        <PublicSecureFileBrandHeader />

        <main className="public-secure-file-card">{renderContent()}</main>

        <footer className="public-secure-file-footer">
          <i className="fas fa-lock" aria-hidden="true" />
          <span>Access is encrypted and logged for security and compliance purposes.</span>
        </footer>
      </div>
    </div>
  );
};

export default PublicSecureFilePage;
